import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Package, Plus, Edit2, Trash2, X, DollarSign, Clock, CreditCard, FileText } from 'lucide-react';
import { BASE_URL } from '../config';
import { notify } from '../utils/toast';

export default function PackageAdmin() {
  const [packages, setPackages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [form, setForm] = useState({
    package_name: '',
    price: '',
    credit_limit: '',
    days_limit: '',
    details: '',
    storage: ''  // ✅ Added storage field
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/packages/all/`);
      setPackages(res.data);
    } catch (err) {
      console.error('Error fetching packages:', err);
      notify.error('Failed to load packages');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('accessToken');
      
      if (editingPackage) {
        // Update existing package
        await axios.put(
          `${BASE_URL}/api/packages/update/${editingPackage.package_ID}/`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        notify.success('Package updated successfully');
      } else {
        // Create new package
        await axios.post(
          `${BASE_URL}/api/packages/create/`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        notify.success('Package created successfully');
      }
      
      setShowModal(false);
      setEditingPackage(null);
      setForm({ package_name: '', price: '', credit_limit: '', days_limit: '', details: '', storage: '' });  // ✅ Added storage
      fetchPackages();
    } catch (err) {
      console.error('Error saving package:', err);
      notify.error('Failed to save package');
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setForm({
      package_name: pkg.package_name,
      price: pkg.price,
      credit_limit: pkg.credit_limit,
      days_limit: pkg.days_limit,
      details: pkg.details || '',
      storage: pkg.storage || ''  // ✅ Added storage field
    });
    setShowModal(true);
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(
        `${BASE_URL}/api/packages/delete/${packageId}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notify.success('Package deleted successfully');
      fetchPackages();
    } catch (err) {
      console.error('Error deleting package:', err);
      notify.error('Failed to delete package');
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Package Management</h1>
          <p className="text-sm text-slate-600">Create and manage subscription packages</p>
        </div>
        <button
          onClick={() => {
            setEditingPackage(null);
            setForm({ package_name: '', price: '', credit_limit: '', days_limit: '', details: '', storage: '' });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-medical-500 hover:bg-medical-600 text-white rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create Package
        </button>
      </div>

      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((pkg) => (
          <motion.div
            key={pkg.package_ID}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-xl font-bold text-slate-900">{pkg.package_name}</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(pkg)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(pkg.package_ID)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-slate-600">
                <DollarSign className="w-4 h-4" />
                <span className="text-2xl font-bold text-medical-600">${pkg.price}</span>
              </div>
              
              <div className="flex items-center gap-2 text-slate-600">
                <CreditCard className="w-4 h-4" />
                <span>{pkg.credit_limit} Credits</span>
              </div>
              
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4" />
                <span>{pkg.days_limit} Days</span>
              </div>

              {pkg.storage && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Package className="w-4 h-4" />
                  <span>{pkg.storage} GB Storage</span>
                </div>
              )}

              {pkg.details && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex items-start gap-2 text-slate-600">
                    <FileText className="w-4 h-4 mt-1" />
                    <p className="text-sm whitespace-pre-line">{pkg.details}</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-900">
                  {editingPackage ? 'Edit Package' : 'Create Package'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Package Name *
                  </label>
                  <input
                    type="text"
                    value={form.package_name}
                    onChange={(e) => setForm({ ...form, package_name: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Price ($) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Credit Limit *
                  </label>
                  <input
                    type="number"
                    value={form.credit_limit}
                    onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Days Limit *
                  </label>
                  <input
                    type="number"
                    value={form.days_limit}
                    onChange={(e) => setForm({ ...form, days_limit: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Storage (GB)
                  </label>
                  <input
                    type="number"
                    value={form.storage}
                    onChange={(e) => setForm({ ...form, storage: e.target.value })}
                    placeholder="Enter storage in GB (optional)"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Storage limit in GB (leave empty if not applicable)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Package Details
                  </label>
                  <textarea
                    value={form.details}
                    onChange={(e) => setForm({ ...form, details: e.target.value })}
                    rows="4"
                    placeholder="Enter package features, storage limits, token limits, etc..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none resize-none"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Describe features, storage, time limits, token limits, etc.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-medical-500 hover:bg-medical-600 text-white rounded-lg transition-colors"
                  >
                    {editingPackage ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
